/* eslint-disable sonarjs/cognitive-complexity */
import { Space, Row, Col, Button, message as toaster } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { CloseCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { FieldArray, Formik } from 'formik';
import { groupBy, isArray, flattenDeep } from 'lodash';
import { Form, Radio, DatePicker, Input as FormInput, Select } from 'formik-antd';
import PropTypes from 'prop-types';
import React, { useState, useEffect, Fragment, useRef } from 'react';
import {
	initialValues,
	getTotalAmount,
	disableDateRanges,
	FormSchema,
	getAmount,
	SaveBtn,
	ClearBtn,
	ConfirmBtn,
	Label,
	BillType,
	addRemovePayment,
	UPIAndNEFTRefCheck,
} from 'components/pages/collection-app/invoice-controller';
import { Text, Box, Flex } from 'atoms';
import {
	ClearConfirmation,
	DuplicateReferenceConfirmation,
	// AutoCompleteUpi,
	ConfirmBeforeUpdate,
} from 'components/pages/collection';
import { Debug } from 'forms/Debug';
import { useQueryParams } from 'hooks';
import { clearInvoiceID, getSalesInvoicesList, saveInvoice } from 'store/salesmanSlice';
import { AutoCompleteUpi } from './UPIAutoComplete';

const { TextArea } = FormInput;
const { Option } = Select;

export default function InvoiceFormfrom({ data, onClose }) {
	const dispatch = useDispatch();
	const { banks = [] } = useSelector((state) => state.invoice);
	const { SalesInvoicesFiltered = [] } = useSelector((state) => state.salesman);

	const [formInitialValues, setFormInitialValues] = useState({
		...initialValues,
		previousStatus: data?.status,
		current_outstanding_amount: Number(data?.initial_outstanding_amount),
		store_id: data?.store_id,
		store_name: data?.store_name,
	});
	const { salesmanId = undefined } = useQueryParams();

	const [showClearConfirmation, setShowClearConfirmation] = useState(false);
	const [showDuplicateError, setShowDuplicateError] = useState(false);
	const [showUpdateConfirmation, setShowUpdateConfirmation] = useState(false);
	const [showUpiPopup, setShowUpiPopup] = useState(false);
	const [UPIRefFieldIndex, setUPIRefFieldIndex] = useState();
	const formRef = useRef();

	useEffect(() => {
		/**
		 * To Prefill the data - On Edit mode
		 */
		if (data?.status !== 'Pending') {
			console.log('edit mode');

			const newInit = {
				status: data?.status,
				remarks: data?.remarks?.trim(),
				current_outstanding_amount: Number(data?.initial_outstanding_amount),
				previousStatus: data?.status,
				paid: data?.paid,
				store_id: data?.store_id,
				store_name: data?.store_name,
			};
			/**
			 * group the payment types by payment_type key
			 */
			const groupedPayments = groupBy(data?.payment, 'payment_type');

			newInit.cash = groupedPayments?.Cash?.[0]?.amount || 0;
			newInit.upiData = groupedPayments?.UPI || initialValues?.upiData;
			newInit.chequeData = groupedPayments?.Cheque || initialValues?.chequeData;
			newInit.neftData = groupedPayments?.NEFT || initialValues?.neftData;
			setFormInitialValues({ ...newInit });
		} else {
			console.log('create mode');

			/**
			 * On Edit Mode
			 */
			setFormInitialValues({
				...initialValues,
				previousStatus: data?.status,
				current_outstanding_amount: Number(data?.initial_outstanding_amount),
				store_id: data?.store_id,
				store_name: data?.store_name,
			});
		}

		return () => {
			setFormInitialValues({ ...initialValues });
		};
	}, [data]);

	function handleClear() {
		dispatch(clearInvoiceID(data?.id))
			.then(() => {
				onClose();
				dispatch(getSalesInvoicesList(salesmanId));
			})
			.finally(() => {
				setFormInitialValues({ ...initialValues });
			});
	}

	function handleSubmit(values, { setErrors, setSubmitting }) {
		console.log(values, 'values');
		if (values?.previousStatus !== 'Pending') {
			/**
			 * On update mode ask confirmation before update
			 */
			setShowUpdateConfirmation(true);
			setSubmitting(false);
		} else {
			const payment = [];

			/**
			 * Validation for same store with cash payment of more than 199999
			 */
			let sameStore = SalesInvoicesFiltered?.filter(
				(item) => item?.store_id === values?.store_id
			)
				?.map((item) => item?.payment?.filter((it) => it?.payment_type === 'Cash'))
				?.filter((ei) => ei.length > 0);

			sameStore = flattenDeep(sameStore).reduce((acc, curr) => acc + curr.amount, 0);
			sameStore += values.cash;

			if (sameStore > 199999) {
				toaster.error('Cash payment more than 199999 is not allowed for same store');
				setSubmitting(false);
			} else {
				if (values.cash !== '' && Number(values.cash) > 0) {
					payment.push({
						payment_type: 'Cash',
						amount: Number(values.cash),
					});
				}

				// if (Number(values?.upiData[0]?.amount) > 0) {}
				if (values?.upiData.length > 0) {
					values?.upiData?.forEach((item) => {
						if (item?.reference_number && item?.amount) {
							payment.push({
								payment_type: 'UPI',
								reference_number: item.reference_number.toString(),
								amount: Number(item.amount),
								bank_statement_id: item.bank_statement_id,
							});
						}
					});
				}

				// if (Number(values?.chequeData[0]?.amount) > 0) {}
				if (values?.chequeData.length > 0) {
					values?.chequeData?.forEach((item) => {
						if (
							item?.reference_number &&
							item?.due_date &&
							item?.amount &&
							item?.bank_id
						) {
							payment.push({
								payment_type: 'Cheque',
								reference_number: item.reference_number.toString(),
								due_date: moment(item.due_date).format('YYYY-MM-DD'),
								amount: Number(item.amount),
								bank_id: Number(item.bank_id),
							});
						}
					});
				}

				// if (Number(values?.neftData[0]?.amount) > 0) {}
				if (values?.neftData.length > 0) {
					values?.neftData?.forEach((item) => {
						if (item?.reference_number && item?.amount) {
							payment.push({
								payment_type: 'NEFT',
								reference_number: item.reference_number.toString(),
								amount: Number(item.amount),
							});
						}
					});
				}

				const formValues = {
					previousStatus: values?.previousStatus,
					status: values.status,
					remarks: values.remarks?.trim(),
					payment,
				};

				console.log(formValues, 'formValues');

				dispatch(saveInvoice(`${data?.id}`, formValues, setErrors))
					// eslint-disable-next-line sonarjs/no-identical-functions
					.then(() => {
						onClose();
						dispatch(getSalesInvoicesList(salesmanId));
					})
					.finally(() => {
						setSubmitting(false);
					});
			}
		}
	}

	const handleCancel = () => {
		setShowDuplicateError(false);
	};

	const handleOk = () => {
		if (formRef?.current) {
			setShowDuplicateError(false);
			formRef.current.handleSubmit();
		}
	};

	const handleUpdateCancel = () => {
		setShowUpdateConfirmation(false);
	};

	const handleUpdateOk = () => {
		dispatch(clearInvoiceID(data?.id, false)).then(() => {
			/**
			 * On update first clear the invoice to fresh state
			 */
			if (formRef?.current) {
				formRef.current?.setFieldValue('previousStatus', 'Pending');
				setShowUpdateConfirmation(false);
				formRef.current.handleSubmit();
			}
		});
	};

	// const triggerFormValidate = () => {
	// 	if (formRef?.current) {
	// 		formRef.current.validateForm();
	// 	}
	// };

	return (
		<>
			<Formik
				enableReinitialize
				initialValues={formInitialValues}
				validationSchema={FormSchema}
				onSubmit={handleSubmit}
				initialTouched={{
					remarks: true,
					upiData: true,
					chequeData: true,
					neftData: true,
				}}
				innerRef={formRef}
			>
				{({ values, errors, setFieldValue, isValid }) => (
					<Form
						layout="vertical"
						hideRequiredMark
						className={
							data?.verified_by_cashier_id ?? data?.delegated_salesman_name
								? 'invoice-formik-form disabled-div'
								: 'invoice-formik-form'
						}
					>
						<Row justify="end" align="middle">
							knjjn
							<Col span={20} className="invoice-form">
								<Row
									justify="space-around"
									align="middle"
									className="invoice-form-first-row"
								>
									<Col span={8}>
										<BillType
											isValidAmount={getAmount(values.cash)}
											className="btlr-4"
										>
											Cash
										</BillType>
									</Col>
									<Col span={16}>
										<Form.Item
											name="cash"
											hasFeedback={false}
											showValidateSuccess={false}
										>
											<FormInput
												type="number"
												min={0}
												prefix="₹"
												name="cash"
											/>
										</Form.Item>
									</Col>
								</Row>
								<FieldArray
									name="upiData"
									render={(arrayHelpers) => (
										<div>
											{values?.upiData?.map((rowData, index) => (
												<Fragment key={rowData?.id}>
													<Row justify="space-around" align="middle">
														<Col span={8} className="add-section">
															<BillType
																isValidAmount={getAmount(
																	values.upiData[index]?.amount
																)}
															>
																UPI
															</BillType>
															<Button
																onClick={() =>
																	addRemovePayment(
																		arrayHelpers,
																		index,
																		'upi'
																	)
																}
																className="add-btn"
																type="button"
															>
																{index === 0 ? '+' : '-'}
															</Button>
														</Col>
														<Col span={16}>
															<Form.Item
																name={`upiData[${index}].amount`}
																hasFeedback={false}
																showValidateSuccess={false}
															>
																<FormInput
																	type="number"
																	min={0}
																	prefix="₹"
																	name={`upiData[${index}].amount`}
																	onChange={(e) => {
																		if (
																			e.target.value ===
																				'0' ||
																			e.target.value === ''
																		) {
																			setFieldValue(
																				`upiData[${index}].reference_number`,
																				undefined
																			);
																			setFieldValue(
																				`upiData[${index}].bank_statement_id`,
																				undefined
																			);
																		}
																	}}
																/>
															</Form.Item>
														</Col>
													</Row>
													{getAmount(values.upiData[index]?.amount) && (
														<Row>
															<Col
																span={24}
																className="pt-10 px-10 no-border"
															>
																<Box
																	width="50%"
																	className="invoice-form-remarks"
																>
																	<Form.Item
																		name={`upiData[${index}].reference_number`}
																		hasFeedback={false}
																		showValidateSuccess={false}
																		label="*Ref no:"
																		className="custom-label"
																		labelCol={{ span: 24 }}
																		wrapperCol={{ span: 24 }}
																	>
																		<FormInput
																			name={`upiData[${index}].reference_number`}
																			placeholder="last 6 digits"
																			maxLength={6}
																			onClick={() => {
																				setShowUpiPopup(
																					true
																				);
																				setUPIRefFieldIndex(
																					index
																				);
																			}}
																		/>

																		{values.upiData[index]
																			.reference_number && (
																			<Box
																				className="clear__icon"
																				onClick={() => {
																					setFieldValue(
																						`upiData[${index}].reference_number`,
																						''
																					);
																					setFieldValue(
																						`upiData[${index}].bank_statement_id`,
																						''
																					);
																				}}
																			>
																				<CloseCircleOutlined />
																			</Box>
																		)}
																	</Form.Item>
																</Box>
															</Col>
														</Row>
													)}
												</Fragment>
											))}
										</div>
									)}
								/>
								<FieldArray
									name="chequeData"
									render={(arrayHelpers) => (
										<div>
											{values?.chequeData?.map((rowData, index) => (
												<Fragment key={rowData?.id}>
													<Row justify="space-around" align="middle">
														<Col span={8} className="add-section">
															<BillType
																isValidAmount={getAmount(
																	values.chequeData[index]?.amount
																)}
															>
																Cheque
															</BillType>
															<Button
																onClick={() =>
																	addRemovePayment(
																		arrayHelpers,
																		index,
																		'cheque'
																	)
																}
																className="add-btn"
																type="button"
															>
																{index === 0 ? '+' : '-'}
															</Button>
														</Col>
														<Col span={16}>
															<Form.Item
																name={`chequeData[${index}].amount`}
																hasFeedback={false}
																showValidateSuccess={false}
															>
																<FormInput
																	type="number"
																	min={0}
																	prefix="₹"
																	name={`chequeData[${index}].amount`}
																	onChange={(e) => {
																		if (
																			e.target.value ===
																				'0' ||
																			e.target.value === ''
																		) {
																			setFieldValue(
																				`chequeData[${index}].reference_number`,
																				undefined
																			);
																			setFieldValue(
																				`chequeData[${index}].bank_id`,
																				undefined
																			);
																			setFieldValue(
																				`chequeData[${index}].due_date`,
																				undefined
																			);
																		}
																	}}
																/>
															</Form.Item>
														</Col>
													</Row>
													{getAmount(
														values.chequeData[index]?.amount
													) && (
														<Row>
															<Col
																span={24}
																className="p-10 no-border"
															>
																<div className="invoice-form-controls">
																	<Row
																		justify="space-between"
																		align="middle"
																	>
																		<Col span={11}>
																			<div className="invoice-form-remarks">
																				<Form.Item
																					name={`chequeData[${index}].reference_number`}
																					hasFeedback={
																						false
																					}
																					showValidateSuccess={
																						false
																					}
																					label="*Cheque no:"
																					className="custom-label"
																					labelCol={{
																						span: 24,
																					}}
																					wrapperCol={{
																						span: 24,
																					}}
																				>
																					<FormInput
																						name={`chequeData[${index}].reference_number`}
																						placeholder="last 6 digits"
																						pattern="[0-9]*"
																						inputMode="numeric"
																						maxLength={
																							6
																						}
																					/>
																				</Form.Item>
																			</div>
																		</Col>
																		<Col
																			span={12}
																			className="invoice-form-remarks"
																		>
																			<Form.Item
																				name={`chequeData[${index}].bank_id`}
																				hasFeedback={false}
																				showValidateSuccess={
																					false
																				}
																				label="*Bank Name"
																				className="custom-label"
																				labelCol={{
																					span: 24,
																				}}
																				wrapperCol={{
																					span: 24,
																				}}
																			>
																				<Select
																					getPopupContainer={(
																						trigger
																					) =>
																						trigger.parentNode
																					}
																					name={`chequeData[${index}].bank_id`}
																					showSearch
																					optionFilterProp="children"
																					className="w-100"
																					placeholder="*Bank Name"
																					allowClear
																					filterOption={(
																						input,
																						option
																					) =>
																						option.children
																							.toLowerCase()
																							.indexOf(
																								input.toLowerCase()
																							) >= 0
																					}
																				>
																					{banks?.map(
																						(bank) => (
																							<Option
																								key={
																									bank.id
																								}
																								value={
																									bank?.id
																								}
																							>
																								{
																									bank?.name
																								}
																							</Option>
																						)
																					)}
																				</Select>
																			</Form.Item>
																		</Col>
																	</Row>
																	<Row
																		justify="space-between"
																		align="middle"
																	>
																		<Col span={11}></Col>
																		<Col span={12}>
																			<Form.Item
																				name={`chequeData[${index}].due_date`}
																				hasFeedback={false}
																				showValidateSuccess={
																					false
																				}
																				label="*Due Date"
																				className="custom-label"
																				labelCol={{
																					span: 24,
																				}}
																				wrapperCol={{
																					span: 24,
																				}}
																			>
																				<DatePicker
																					getPopupContainer={(
																						trigger
																					) =>
																						trigger.parentNode
																					}
																					name={`chequeData[${index}].due_date`}
																					className="w-100"
																					format="DD MMMM YYYY"
																					allowClear
																					disabledDate={disableDateRanges()}
																				/>
																			</Form.Item>
																		</Col>
																	</Row>
																</div>
															</Col>
														</Row>
													)}
												</Fragment>
											))}
										</div>
									)}
								/>
								<FieldArray
									name="neftData"
									render={(arrayHelpers) => (
										<div>
											{values?.neftData?.map((rowData, index) => (
												<Fragment key={rowData?.id}>
													<Row
														justify="space-around"
														align="middle"
														className={
															values.neftData.length - 1 === index
																? 'invoice-form-last-row'
																: 'invoice-form'
														}
													>
														<Col span={8} className="add-section">
															<BillType
																isValidAmount={getAmount(
																	values.neftData[index]?.amount
																)}
															>
																NEFT
															</BillType>
															<Button
																onClick={() =>
																	addRemovePayment(
																		arrayHelpers,
																		index,
																		'neft'
																	)
																}
																className="add-btn"
																type="button"
															>
																{index === 0 ? '+' : '-'}
															</Button>
														</Col>
														<Col span={16}>
															<Form.Item
																name={`neftData[${index}].amount`}
																hasFeedback={false}
																showValidateSuccess={false}
															>
																<FormInput
																	type="number"
																	min={0}
																	prefix="₹"
																	name={`neftData[${index}].amount`}
																	onChange={(e) => {
																		if (
																			e.target.value ===
																				'0' ||
																			e.target.value === ''
																		) {
																			setFieldValue(
																				`neftData[${index}].reference_number`,
																				undefined
																			);
																		}
																	}}
																/>
															</Form.Item>
														</Col>
													</Row>
													{getAmount(values.neftData[index]?.amount) && (
														<Row
															justify="space-around"
															align="middle"
															className={
																values.neftData.length - 1 === index
																	? 'invoice-form-last-row'
																	: 'invoice-form'
															}
														>
															<Col
																span={24}
																className="pt-10 px-10 no-border"
															>
																<Box
																	width="50%"
																	className="invoice-form-remarks"
																>
																	<Form.Item
																		name={`neftData[${index}].reference_number`}
																		hasFeedback={false}
																		showValidateSuccess={false}
																		label="*Ref no:"
																		className="custom-label"
																		labelCol={{ span: 24 }}
																		wrapperCol={{ span: 24 }}
																	>
																		<FormInput
																			name={`neftData[${index}].reference_number`}
																			placeholder="last 6 digits"
																			pattern="[0-9]*"
																			inputMode="numeric"
																			maxLength={6}
																		/>
																	</Form.Item>
																</Box>
															</Col>
														</Row>
													)}
												</Fragment>
											))}
										</div>
									)}
								/>
							</Col>
						</Row>
						<Row>
							<Col span={24} className="invoice-form-controls right mt-15">
								<Form.Item
									name="status"
									hasFeedback={false}
									showValidateSuccess={false}
								>
									<Radio.Group
										name="status"
										size="large"
										disabled={data?.verified_by_segregator_id}
									>
										<Space direction="horizontal" className="custom-radio">
											<Radio value="Bill Back">
												<Label>
													{data?.billtype === 'Bill'
														? 'Bill Back'
														: 'Voucher Back'}
												</Label>
											</Radio>
											<Radio value="No Bill Back">
												<Label>
													{data?.billtype === 'Bill'
														? 'No Bill Back'
														: 'No Voucher Back'}
												</Label>
											</Radio>
										</Space>
									</Radio.Group>
								</Form.Item>
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<div className="invoice-form-remarks">
									<Form.Item
										name="remarks"
										label="Remarks"
										hasFeedback={false}
										showValidateSuccess={false}
										className="custom-label"
										labelCol={{ span: 24 }}
										wrapperCol={{ span: 24 }}
									>
										<TextArea
											name="remarks"
											placeholder="Remarks"
											maxLength={170}
											style={{ height: 40 }}
										/>
									</Form.Item>
								</div>
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<Text fontSize="12px" fontWeight="300" py="10px" textAlign="left">
									Total: <span>₹{getTotalAmount(values)}</span>
								</Text>
							</Col>
						</Row>

						{!isArray(errors?.chequeData) && (
							<Flex justifyContent="center" alignItems="center">
								<Text
									as="p"
									color="red"
									fontSize="12px"
									maxWidth="250px"
									textAlign="center"
								>
									{errors?.chequeData}
								</Text>
							</Flex>
						)}

						{UPIAndNEFTRefCheck(values) && (
							<Flex justifyContent="center" alignItems="center">
								<Text
									as="p"
									color="red"
									fontSize="12px"
									maxWidth="250px"
									textAlign="center"
								>
									* Duplicate NEFT/UPI Reference numbers, please check it
								</Text>
							</Flex>
						)}

						{!data?.verified_by_cashier_id && (
							<Row className="mt-15" justify="space-around" align="middle">
								<Col span={12} className="left">
									{data?.status !== 'Pending' && (
										<>
											<ClearConfirmation
												visible={showClearConfirmation}
												onClose={() => setShowClearConfirmation(false)}
												onClear={handleClear}
											/>
											{!data?.verified_by_segregator_id && (
												<ClearBtn
													type="button"
													onClick={() => setShowClearConfirmation(true)}
												>
													Clear
												</ClearBtn>
											)}
										</>
									)}
								</Col>
								<Col span={12} className="right">
									{UPIAndNEFTRefCheck(values) ? (
										<ConfirmBtn
											disabled={!isValid}
											onClick={() => setShowDuplicateError(true)}
										>
											{data?.status === 'Pending' ? 'Save' : 'Update'}
										</ConfirmBtn>
									) : (
										<SaveBtn>
											{data?.status === 'Pending' ? 'Save' : 'Update'}
										</SaveBtn>
									)}
								</Col>
							</Row>
						)}
						{process.env.NODE_ENV === 'development' && <Debug show={false} />}
					</Form>
				)}
			</Formik>
			<DuplicateReferenceConfirmation
				visible={showDuplicateError}
				handleOk={handleOk}
				handleCancel={handleCancel}
			/>
			<ConfirmBeforeUpdate
				visible={showUpdateConfirmation}
				handleOk={handleUpdateOk}
				handleCancel={handleUpdateCancel}
			/>
			{showUpiPopup && (
				<AutoCompleteUpi
					setShowUpiPopup={setShowUpiPopup}
					showUpiPopup={showUpiPopup}
					storeData={data}
					formRef={formRef}
					UPIRefFieldIndex={UPIRefFieldIndex}
				/>
			)}
		</>
	);
}

InvoiceFormfrom.propTypes = {
	data: PropTypes.shape({
		status: PropTypes.string,
		remarks: PropTypes.string,
		paid: PropTypes.number,
		billtype: PropTypes.string,
		payment: PropTypes.array,
		amount: PropTypes.number,
		initial_outstanding_amount: PropTypes.string,
		current_outstanding_amount: PropTypes.string,
		verified_by_segregator_id: PropTypes.number,
		verified_by_cashier_id: PropTypes.number,
		delegated_salesman_name: PropTypes.string,
		id: PropTypes.number,
		store_id: PropTypes.number,
		store_name: PropTypes.string,
	}),
	onClose: PropTypes.func.isRequired,
};
